import { useEffect, useRef } from 'react';

type Enabled = boolean | (() => boolean);

/**
 * Prevents refresh/close when enabled.
 *
 * Key detail for your case:
 * - some libs attach their own beforeunload listeners (disconnect/cleanup) and they can fire
 *   even when user clicks "Cancel"; so we attach in capture phase AND override window.onbeforeunload,
 *   and we stop propagation when blocking.
 */
export const useBeforeUnload = (enabled: Enabled = true): void => {
  const enabledRef = useRef<Enabled>(enabled);
  const prevOnBeforeUnloadRef = useRef<((e: BeforeUnloadEvent) => any) | null>(null);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  useEffect(() => {
    const isEnabled = (): boolean => {
      const v = enabledRef.current;
      try {
        return typeof v === 'function' ? Boolean(v()) : Boolean(v);
      } catch (err) {
        return false;
      }
    };

    const handler = (e: BeforeUnloadEvent) => {
      const shouldBlock = isEnabled();

      if (!shouldBlock) return;

      try {
        (e as any).stopImmediatePropagation?.();
        e.stopPropagation?.();
      } catch {}

      // Required for Chrome; returnValue must be set
      e.preventDefault();
      e.returnValue = '';
      return '';
    };

    prevOnBeforeUnloadRef.current = window.onbeforeunload as any;
    window.onbeforeunload = handler as any;

    window.addEventListener('beforeunload', handler, { capture: true });

    return () => {
      window.removeEventListener('beforeunload', handler, { capture: true } as any);

      window.onbeforeunload = prevOnBeforeUnloadRef.current as any;
      prevOnBeforeUnloadRef.current = null;
    };
  }, []);
};
